import { Theme, StylesConfig, GroupBase } from 'react-select';
import { isDarkMode } from '@/hooks/useDarkMode';

export const selectTheme = (theme: Theme): Theme => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary: '#3B82F6', // Blue-500
        primary75: '#60A5FA', // Blue-400
        primary50: '#93C5FD', // Blue-300
        primary25: '#DBEAFE', // Blue-100
        danger: '#EF4444', // Red-500
        dangerLight: '#FEE2E2', // Red-100
        neutral0: '#FFFFFF', // White
        neutral5: '#F9FAFB', // Gray-50
        neutral10: '#F3F4F6', // Gray-100
        neutral20: '#E5E7EB', // Gray-200
        neutral30: '#D1D5DB', // Gray-300
        neutral40: '#9CA3AF', // Gray-400
        neutral50: '#6B7280', // Gray-500
        neutral60: '#4B5563', // Gray-600
        neutral70: '#374151', // Gray-700
        neutral80: '#1F2937', // Gray-800
        neutral90: '#111827', // Gray-900
    },
    spacing: {
        ...theme.spacing,
        controlHeight: 44,
        baseUnit: 4,
    },
});

export const selectStyles: StylesConfig<any, any, any> = {
    control: (base, state) => {
        const dark = isDarkMode();
        return {
            ...base,
            minHeight: '44px',
            borderRadius: '6px',
            borderColor: state.isFocused ? '#60A5FA' : (dark ? '#4B5563' : '#E5E7EB'),
            borderWidth: '1px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(96, 165, 250, 0.1)' : 'none',
            backgroundColor: dark ? '#374151' : '#FFFFFF',
            '&:hover': {
                borderColor: state.isFocused ? '#60A5FA' : (dark ? '#6B7280' : '#D1D5DB'),
            },
            transition: 'all 0.2s ease',
        };
    },
    menu: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: `1px solid ${dark ? '#4B5563' : '#E5E7EB'}`,
            backgroundColor: dark ? '#1F2937' : '#FFFFFF',
            overflow: 'hidden',
            marginTop: '4px',
        };
    },
    menuList: (base) => ({
        ...base,
        padding: '8px',
        maxHeight: '300px',
    }),
    option: (base, state) => {
        const dark = isDarkMode();
        return {
            ...base,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '2px',
            fontSize: '14px',
            fontWeight: state.isSelected ? '500' : '400',
            backgroundColor: state.isDisabled
                ? 'transparent'
                : state.isSelected
                    ? (dark ? '#1E3A8A' : '#EFF6FF')
                    : state.isFocused
                        ? (dark ? '#374151' : '#F9FAFB')
                        : 'transparent',
            color: state.isDisabled
                ? (dark ? '#6B7280' : '#9CA3AF')
                : state.isSelected
                    ? (dark ? '#93C5FD' : '#2563EB')
                    : (dark ? '#E5E7EB' : '#374151'),
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease',
            position: 'relative',
            '&:active': {
                backgroundColor: state.isDisabled
                    ? 'transparent'
                    : state.isSelected
                        ? (dark ? '#1E40AF' : '#DBEAFE')
                        : (dark ? '#4B5563' : '#F3F4F6'),
            },
            '&::after': state.isSelected && !state.isDisabled ? {
                content: '"✓"',
                color: dark ? '#93C5FD' : '#60A5FA',
                fontWeight: '600',
                fontSize: '16px',
                marginLeft: 'auto',
            } : {},
        };
    },
    placeholder: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            color: dark ? '#9CA3AF' : '#9CA3AF',
            fontSize: '14px',
        };
    },
    singleValue: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            color: dark ? '#E5E7EB' : '#374151',
            fontSize: '14px',
            fontWeight: '400',
        };
    },
    input: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            color: dark ? '#E5E7EB' : '#374151',
            fontSize: '14px',
            'input:focus': {
                boxShadow: 'none',
            },
        };
    },
    indicatorSeparator: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            backgroundColor: dark ? '#4B5563' : '#E5E7EB',
        };
    },
    dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isFocused ? '#60A5FA' : '#9CA3AF',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#60A5FA',
        },
    }),
    clearIndicator: (base, state) => ({
        ...base,
        color: state.isFocused ? '#60A5FA' : '#bcc4d1',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#60A5FA',
        }
    }),
    multiValue: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            backgroundColor: dark ? '#374151' : '#ffffff',
            borderRadius: '6px',
            border: `1px solid ${dark ? '#4B5563' : '#E5E7EB'}`,
            marginLeft: '3px',
            marginRight: '3px',
            marginTop:'3px',
            marginBottom:'3px',
        };
    },
    multiValueLabel: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            color: dark ? '#E5E7EB' : '#374151',
            fontSize: '14px',
            fontWeight: '400',
            padding: '2px 4px',
            paddingLeft: '8px',
        };
    },
    multiValueRemove: (base) => {
        const dark = isDarkMode();
        return {
            ...base,
            color: dark ? '#9CA3AF' : '#9CA3AF',
            borderRadius: '0 4px 4px 0',
            padding: '4px 6px',
            '&:hover': {
                backgroundColor: dark ? '#4B5563' : '#f1f1f1',
                color: '#9CA3AF',
            },
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        };
    },

    valueContainer: (base, state) => ({
        ...base,

    }),
};

export function getVariantStyles(variant: string): StylesConfig<any, any, any> {
    switch (variant) {
        case 'modal':
            return {
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                }),
                menu: (base) => ({
                    ...base,
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px -5px rgba(0, 0, 0, 0.15), 0 15px 15px -5px rgba(0, 0, 0, 0.1)',
                }),
            };
        case 'inline':
            return {
                control: (base) => ({
                    ...base,
                    minHeight: '36px',
                    borderRadius: '6px',
                }),
                option: (base, state) => ({
                    ...base,
                    padding: '8px 12px',
                }),
            };
        default:
            return {};
    }
}
